/**
 * localMetricsSeries.ts — Series temporales locales (ventas, gastos, tienda,
 * horas pico).
 *
 * QUÉ HACE: genera historial de ventas y actividad por hora, y los datasets de
 *   los gráficos de Caja (ventas y gastos), Tienda virtual y Horas Pico a
 *   partir de LS para el periodo/fecha solicitados.
 *
 * QUÉ INVOCA: helpers de localMetricsShared (lecturas de storage, fechas,
 *   cortes por periodo) y date-fns.
 *
 * QUÉ EXPORTA: computeLocalSalesHistory, computeLocalHourlyActivity,
 *   computeChartVentasCaja, computeChartGastosCaja, computeChartTiendaCaja,
 *   LocalPeakHourPoint, computeLocalPeakHours.
 *
 * QUÉ NO DEBE HACER: UI ni HTTP.
 */
import {
  format,
  subDays,
  startOfMonth,
  startOfDay,
  startOfWeek,
  addDays,
  isSameDay,
  getMonth,
  getYear,
  getHours,
} from "date-fns";
import { es } from "date-fns/locale";
import type { ChartDataPoint } from "@/lib/utils/chart-data";
import type { MetricsPeriod, SalesPoint } from "../types/metrics.types";
import {
  BUSINESS_HOURS,
  cap,
  movFecha,
  movimientosGastos,
  pedidoFecha,
  pedidosActivos,
  periodFilterRange,
  pointRange,
  readMovimientosLocal,
  readPedidosLocal,
  readVentasLocal,
  ventaFecha,
  ventasActivas,
  withinPeriod,
  type ChartPeriod,
  type PeakHoursPeriod,
} from "./localMetricsShared";

export function computeLocalSalesHistory(period: MetricsPeriod): SalesPoint[] {
  const ventas = ventasActivas(readVentasLocal());
  const now = new Date();

  if (period === "today") {
    const hoy = startOfDay(now);
    const byHour: Record<number, { total: number; cantidad: number }> = {};
    ventas
      .filter((v) => isSameDay(ventaFecha(v), hoy))
      .forEach((v) => {
        const h = ventaFecha(v).getHours();
        if (!byHour[h]) byHour[h] = { total: 0, cantidad: 0 };
        byHour[h].total += v.total || 0;
        byHour[h].cantidad += 1;
      });
    return Object.keys(byHour)
      .map(Number)
      .sort((a, b) => a - b)
      .map((h) => ({
        name: `${h}:00`,
        total: byHour[h].total,
        cantidad: byHour[h].cantidad,
      }));
  }

  const start = period === "week" ? subDays(now, 7) : startOfMonth(now);

  const filtered = ventas.filter((v) => ventaFecha(v) >= start);

  if (period === "week") {
    const acc: SalesPoint[] = [];
    filtered.forEach((v) => {
      const dateKey = format(ventaFecha(v), "EEE dd");
      const existing = acc.find((item) => item.name === dateKey);
      if (existing) {
        existing.total += v.total || 0;
        existing.cantidad = (existing.cantidad || 0) + 1;
      } else {
        acc.push({ name: dateKey, total: v.total || 0, cantidad: 1 });
      }
    });
    return acc;
  }

  const grouped: Record<string, number> = {};
  filtered.forEach((v) => {
    const dateKey = format(ventaFecha(v), "dd MMM");
    grouped[dateKey] = (grouped[dateKey] || 0) + (v.total || 0);
  });
  return Object.keys(grouped).map((k) => ({ name: k, total: grouped[k] }));
}

export function computeLocalHourlyActivity(): SalesPoint[] {
  return computeLocalSalesHistory("today");
}

/**
 * Transacciones (caja + web) por mes, para el KPI "Transacciones" (vista mensual).
 * Devuelve `count` meses (más antiguo → actual) con `total` = nº de transacciones.
 */
export function computeLocalMonthlyTransactions(count = 6): SalesPoint[] {
  const ventas = ventasActivas(readVentasLocal());
  const pedidos = pedidosActivos(readPedidosLocal());
  const now = new Date();
  return Array.from({ length: count }, (_, index) => {
    const d = new Date(
      now.getFullYear(),
      now.getMonth() - (count - 1 - index),
      1,
    );
    const year = d.getFullYear();
    const month = d.getMonth();
    const caja = ventas.filter((venta) => {
      const fecha = ventaFecha(venta);
      return getYear(fecha) === year && getMonth(fecha) === month;
    }).length;
    const web = pedidos.filter((pedido) => {
      const fecha = pedidoFecha(pedido);
      return getYear(fecha) === year && getMonth(fecha) === month;
    }).length;
    return { name: format(d, "MMM", { locale: es }), total: caja + web };
  });
}

/**
 * Genera datos de ventas (caja) reales desde localStorage agrupados por periodo.
 * Si no hay datos para el periodo → retorna array vacío (señal de fallback demo).
 */
export function computeChartVentasCaja(
  period: ChartPeriod,
  date: Date,
): ChartDataPoint[] {
  const ventas = ventasActivas(readVentasLocal());

  if (period === "hoy") {
    const hoy = startOfDay(date);
    const horas = Array.from({ length: 24 }, (_, h) => h);
    const byHour: Record<number, number> = {};
    ventas
      .filter((v) => isSameDay(ventaFecha(v), hoy))
      .forEach((v) => {
        const h = getHours(ventaFecha(v));
        byHour[h] = (byHour[h] ?? 0) + (v.total || 0);
      });
    return horas.map((h) => {
      const hourDate = new Date(date);
      hourDate.setHours(h, 0, 0, 0);
      return {
        name: format(hourDate, "h a", { locale: es })
          .replace("a. m.", "am")
          .replace("p. m.", "pm"),
        fullDate: hourDate,
        total: byHour[h] ?? 0,
        type: "hour" as const,
        ...pointRange(hoy, hoy),
      };
    });
  }

  if (period === "semana") {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(weekStart, i);
      const total = ventas
        .filter((v) => isSameDay(ventaFecha(v), d))
        .reduce((s, v) => s + (v.total || 0), 0);
      const short = format(d, "EEE", { locale: es });
      return {
        name: format(d, "EEEE", { locale: es }),
        // Vista semanal unificada: solo el día ("lun", "mar"…), sin el número,
        // igual que en la Evolución de métodos de pago.
        shortName: short,
        fullName: `${cap(format(d, "EEEE", { locale: es }))} ${d.getDate()}`,
        fullDate: d,
        total,
        type: "day" as const,
        ...pointRange(d, d),
      };
    });
  }

  if (period === "mes") {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => {
      const d = new Date(year, month, i + 1);
      const total = ventas
        .filter((v) => isSameDay(ventaFecha(v), d))
        .reduce((s, v) => s + (v.total || 0), 0);
      return {
        name: String(i + 1),
        shortName: String(i + 1),
        fullName: `${d.getDate()} de ${cap(format(d, "MMMM", { locale: es }))}`,
        fullDate: d,
        total,
        type: "day" as const,
        ...pointRange(d, d),
      };
    });
  }

  // "anio"
  const year = date.getFullYear();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(year, i, 1);
    const total = ventas
      .filter(
        (v) => getMonth(ventaFecha(v)) === i && getYear(ventaFecha(v)) === year,
      )
      .reduce((s, v) => s + (v.total || 0), 0);
    return {
      name: format(d, "MMM", { locale: es }),
      fullName:
        format(d, "MMMM", { locale: es }).charAt(0).toUpperCase() +
        format(d, "MMMM", { locale: es }).slice(1) +
        ` ${year}`,
      fullDate: d,
      total,
      type: "month" as const,
      ...pointRange(d, new Date(year, i + 1, 0)),
    };
  });
}

/**
 * Genera datos de gastos (tipo gasto | retiro, no anulados) desde localStorage.
 * Si no hay datos para el periodo → retorna array vacío (señal de fallback demo).
 */
export function computeChartGastosCaja(
  period: ChartPeriod,
  date: Date,
): ChartDataPoint[] {
  const movs = movimientosGastos(readMovimientosLocal());

  if (period === "hoy") {
    const hoy = startOfDay(date);
    const horas = Array.from({ length: 24 }, (_, h) => h);
    const byHour: Record<number, number> = {};
    movs
      .filter((m) => isSameDay(movFecha(m), hoy))
      .forEach((m) => {
        const h = getHours(movFecha(m));
        byHour[h] = (byHour[h] ?? 0) + (m.monto || 0);
      });
    return horas.map((h) => {
      const hourDate = new Date(date);
      hourDate.setHours(h, 0, 0, 0);
      return {
        name: format(hourDate, "h a", { locale: es })
          .replace("a. m.", "am")
          .replace("p. m.", "pm"),
        fullDate: hourDate,
        total: byHour[h] ?? 0,
        type: "hour" as const,
        ...pointRange(hoy, hoy),
      };
    });
  }

  if (period === "semana") {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(weekStart, i);
      const total = movs
        .filter((m) => isSameDay(movFecha(m), d))
        .reduce((s, m) => s + (m.monto || 0), 0);
      return {
        name: format(d, "EEEE", { locale: es }),
        shortName: format(d, "EEE", { locale: es }),
        fullDate: d,
        total,
        type: "day" as const,
        ...pointRange(d, d),
      };
    });
  }

  if (period === "mes") {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => {
      const d = new Date(year, month, i + 1);
      const total = movs
        .filter((m) => isSameDay(movFecha(m), d))
        .reduce((s, m) => s + (m.monto || 0), 0);
      return {
        name: String(i + 1),
        shortName: String(i + 1),
        fullName: `${d.getDate()} de ${cap(format(d, "MMMM", { locale: es }))}`,
        fullDate: d,
        total,
        type: "day" as const,
        ...pointRange(d, d),
      };
    });
  }

  // "anio"
  const year = date.getFullYear();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(year, i, 1);
    const total = movs
      .filter(
        (m) => getMonth(movFecha(m)) === i && getYear(movFecha(m)) === year,
      )
      .reduce((s, m) => s + (m.monto || 0), 0);
    return {
      name: format(d, "MMM", { locale: es }),
      fullName:
        format(d, "MMMM", { locale: es }).charAt(0).toUpperCase() +
        format(d, "MMMM", { locale: es }).slice(1) +
        ` ${year}`,
      fullDate: d,
      total,
      type: "month" as const,
      ...pointRange(d, new Date(year, i + 1, 0)),
    };
  });
}

/**
 * Genera datos de ventas de tienda virtual (tienda_pedidos) reales desde localStorage.
 * Filtra pedidos no cancelados. Si no hay pedidos → retorna array con totales en 0
 * (NO fallback demo — permite que el gráfico muestre la realidad: sin pedidos aún).
 */
export function computeChartTiendaCaja(
  period: ChartPeriod,
  date: Date,
): ChartDataPoint[] {
  const pedidos = pedidosActivos(readPedidosLocal());

  if (period === "hoy") {
    const hoy = startOfDay(date);
    const horas = Array.from({ length: 24 }, (_, h) => h);
    const byHour: Record<number, number> = {};
    pedidos
      .filter((p) => isSameDay(pedidoFecha(p), hoy))
      .forEach((p) => {
        const h = getHours(pedidoFecha(p));
        byHour[h] = (byHour[h] ?? 0) + (p.total || 0);
      });
    return horas.map((h) => {
      const hourDate = new Date(date);
      hourDate.setHours(h, 0, 0, 0);
      return {
        name: format(hourDate, "h a", { locale: es })
          .replace("a. m.", "am")
          .replace("p. m.", "pm"),
        fullDate: hourDate,
        total: byHour[h] ?? 0,
        type: "hour" as const,
        ...pointRange(hoy, hoy),
      };
    });
  }

  if (period === "semana") {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(weekStart, i);
      const total = pedidos
        .filter((p) => isSameDay(pedidoFecha(p), d))
        .reduce((s, p) => s + (p.total || 0), 0);
      return {
        name: format(d, "EEEE", { locale: es }),
        shortName: format(d, "EEE", { locale: es }),
        fullDate: d,
        total,
        type: "day" as const,
        ...pointRange(d, d),
      };
    });
  }

  if (period === "mes") {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => {
      const d = new Date(year, month, i + 1);
      const total = pedidos
        .filter((p) => isSameDay(pedidoFecha(p), d))
        .reduce((s, p) => s + (p.total || 0), 0);
      return {
        name: String(i + 1),
        shortName: String(i + 1),
        fullName: `${d.getDate()} de ${cap(format(d, "MMMM", { locale: es }))}`,
        fullDate: d,
        total,
        type: "day" as const,
        ...pointRange(d, d),
      };
    });
  }

  // "anio"
  const year = date.getFullYear();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(year, i, 1);
    const total = pedidos
      .filter(
        (p) =>
          getMonth(pedidoFecha(p)) === i && getYear(pedidoFecha(p)) === year,
      )
      .reduce((s, p) => s + (p.total || 0), 0);
    return {
      name: format(d, "MMM", { locale: es }),
      fullName:
        format(d, "MMMM", { locale: es }).charAt(0).toUpperCase() +
        format(d, "MMMM", { locale: es }).slice(1) +
        ` ${year}`,
      fullDate: d,
      total,
      type: "month" as const,
      ...pointRange(d, new Date(year, i + 1, 0)),
    };
  });
}

/* ── Horas Pico reales ───────────────────────────────────────────────────── */

/** Punto de hora pico compatible con PeakHourDataPoint de chart-data.ts */
export interface LocalPeakHourPoint {
  hour: number;
  name: string;
  fullName: string;
  caja: number;
  tienda: number;
  total: number;
}

/**
 * Computa horas pico reales desde caja_ventas_historial (caja) y tienda_pedidos (web).
 * Agrupa por hora del día sumando todos los días del periodo.
 */
export function computeLocalPeakHours(
  period: PeakHoursPeriod,
  referenceDate?: Date,
): LocalPeakHourPoint[] {
  const range = periodFilterRange(period, referenceDate);
  const ventas = ventasActivas(readVentasLocal()).filter((v) =>
    withinPeriod(ventaFecha(v), range),
  );
  const pedidos = pedidosActivos(readPedidosLocal()).filter((p) =>
    withinPeriod(pedidoFecha(p), range),
  );

  const cajaByHour: Record<number, number> = {};
  const tiendaByHour: Record<number, number> = {};

  ventas.forEach((v) => {
    const h = getHours(ventaFecha(v));
    cajaByHour[h] = (cajaByHour[h] ?? 0) + (v.total || 0);
  });
  pedidos.forEach((p) => {
    const h = getHours(pedidoFecha(p));
    tiendaByHour[h] = (tiendaByHour[h] ?? 0) + (p.total || 0);
  });

  return BUSINESS_HOURS.map((hour) => {
    const refDate = new Date();
    refDate.setHours(hour, 0, 0, 0);
    const caja = cajaByHour[hour] ?? 0;
    const tienda = tiendaByHour[hour] ?? 0;
    return {
      hour,
      name: format(refDate, "h a", { locale: es })
        .replace("a. m.", "am")
        .replace("p. m.", "pm"),
      fullName: format(refDate, "h:mm a", { locale: es })
        .replace("a. m.", "a.m.")
        .replace("p. m.", "p.m."),
      caja,
      tienda,
      total: caja + tienda,
    };
  });
}
